/**
 * Cookie consent for nonresident.tax and app.nonresident.tax (Google Consent
 * Mode v2). Pure rules shared by both apps so the choice a visitor makes on
 * the site reads the same in the app:
 *
 * - each Worker sets a host-only `nt_geo` cookie (the Cloudflare country) on
 *   every HTML response; the page reads it to decide whether to show the banner;
 * - the visitor's choice lives in `nt_consent` on `.nonresident.tax` for 180
 *   days, as a compact versioned string (`v1.an1.ad0`);
 * - an inline snippet sets the Consent Mode defaults (denied in the consent
 *   regions, granted elsewhere) and applies a stored choice before GTM loads;
 * - Mixpanel starts opted out until analytics is granted in a consent region.
 *
 * Runs in the browser and on workerd: no Node or DOM-only imports here; the
 * browser-only helpers at the end take the window as a parameter.
 */

export const CONSENT_COOKIE = "nt_consent";
export const GEO_COOKIE = "nt_geo";
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;
/** Dev-only query parameter that fakes the visitor's country (see countryForRequest). */
export const GEO_OVERRIDE_PARAM = "nt_geo";

const CONSENT_COOKIE_DOMAIN = "nonresident.tax";

/**
 * Where we ask before analytics and advertising cookies: the EU (27), the rest
 * of the EEA (Iceland, Liechtenstein, Norway), the UK and Switzerland, plus the
 * EU outermost regions that Cloudflare and Google report under their own code
 * (French Guiana, Guadeloupe, Martinique, Reunion, Mayotte, Saint Martin, Aland).
 */
export const CONSENT_REGIONS: readonly string[] = Object.freeze([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "IS",
  "LI",
  "NO",
  "GB",
  "CH",
  "GF",
  "GP",
  "MQ",
  "RE",
  "YT",
  "MF",
  "AX",
]);

const REGION_SET = new Set(CONSENT_REGIONS);
const COUNTRY_CODE = /^[A-Z]{2}$/;
/** Cloudflare's "unknown" (XX) and Tor (T1) codes: no real country behind them. */
const UNKNOWN_COUNTRIES = new Set(["XX", "T1"]);

/**
 * True when the visitor must be asked first. A country we cannot place
 * (missing, XX, Tor) counts as in-region: the privacy-safe side.
 */
export function isConsentRegion(country: string | null | undefined): boolean {
  const code = (country ?? "").trim().toUpperCase();
  if (!COUNTRY_CODE.test(code) || UNKNOWN_COUNTRIES.has(code)) return true;
  return REGION_SET.has(code);
}

/**
 * The visitor's country for the `nt_geo` cookie: Cloudflare's `request.cf.country`,
 * or `?nt_geo=DE` when the caller allows an override (local dev only; each
 * Worker decides that from its own environment).
 */
export function countryForRequest(input: {
  cfCountry: string | null | undefined;
  url: URL;
  allowOverride: boolean;
}): string {
  if (input.allowOverride) {
    const override = (
      input.url.searchParams.get(GEO_OVERRIDE_PARAM) ?? ""
    ).toUpperCase();
    if (COUNTRY_CODE.test(override)) return override;
  }
  const country = (input.cfCountry ?? "").toUpperCase();
  return COUNTRY_CODE.test(country) || UNKNOWN_COUNTRIES.has(country)
    ? country
    : "XX";
}

/**
 * The `Set-Cookie` value for `nt_geo`: host-only, readable by the page, for the
 * session. Each Worker sets it fresh on every HTML response, so a 304 or a
 * cached page never pins an old country.
 */
export function geoSetCookie(
  country: string,
  options: { secure: boolean },
): string {
  return `${GEO_COOKIE}=${country}; Path=/; SameSite=Lax${options.secure ? "; Secure" : ""}`;
}

export interface ConsentChoice {
  /** Analytics cookies: GA4 (analytics_storage) and Mixpanel. */
  analytics: boolean;
  /** Advertising cookies: ad_storage, ad_user_data, ad_personalization. */
  advertising: boolean;
}

const CONSENT_VALUE = /^v(\d+)\.an([01])\.ad([01])$/;

export function serializeConsent(choice: ConsentChoice): string {
  return `v${CONSENT_VERSION}.an${choice.analytics ? 1 : 0}.ad${choice.advertising ? 1 : 0}`;
}

/** The stored choice, or null when there is none we can trust (garbage or another version). */
export function parseConsent(
  value: string | null | undefined,
): ConsentChoice | null {
  const match = CONSENT_VALUE.exec(value ?? "");
  if (!match || Number(match[1]) !== CONSENT_VERSION) return null;
  return { analytics: match[2] === "1", advertising: match[3] === "1" };
}

/** One cookie's value out of a `document.cookie` / `Cookie` header string. */
export function readCookie(
  cookies: string | null | undefined,
  name: string,
): string | null {
  for (const part of (cookies ?? "").split(";")) {
    const at = part.indexOf("=");
    if (at === -1) continue;
    if (part.slice(0, at).trim() === name) return part.slice(at + 1).trim();
  }
  return null;
}

/**
 * The `document.cookie` assignment for a choice. On nonresident.tax and its
 * subdomains it is shared across all of them, so a choice made on the site
 * holds in the app; anywhere else (localhost) it is host-only, because the
 * browser silently drops a cookie for a domain the page is not on.
 */
export function consentCookie(
  choice: ConsentChoice,
  options: { hostname: string; secure: boolean },
): string {
  const host = options.hostname.toLowerCase();
  const shared =
    host === CONSENT_COOKIE_DOMAIN ||
    host.endsWith(`.${CONSENT_COOKIE_DOMAIN}`);
  return [
    `${CONSENT_COOKIE}=${serializeConsent(choice)}`,
    ...(shared ? [`Domain=.${CONSENT_COOKIE_DOMAIN}`] : []),
    "Path=/",
    `Max-Age=${CONSENT_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
    ...(options.secure ? ["Secure"] : []),
  ].join("; ");
}

/** What applies right now: a stored choice wins; otherwise denied in a consent region, granted elsewhere. */
export function effectiveConsent(
  choice: ConsentChoice | null,
  inRegion: boolean,
): ConsentChoice {
  if (choice) return choice;
  return { analytics: !inRegion, advertising: !inRegion };
}

export function shouldShowBanner(
  choice: ConsentChoice | null,
  inRegion: boolean,
): boolean {
  return inRegion && choice === null;
}

export type ConsentSignal = "granted" | "denied";

export interface GtagConsent {
  analytics_storage: ConsentSignal;
  ad_storage: ConsentSignal;
  ad_user_data: ConsentSignal;
  ad_personalization: ConsentSignal;
}

export function gtagConsent(choice: ConsentChoice): GtagConsent {
  const ads: ConsentSignal = choice.advertising ? "granted" : "denied";
  return {
    analytics_storage: choice.analytics ? "granted" : "denied",
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  };
}

/**
 * The inline snippet that must run before the GTM loader on every page: the
 * Consent Mode v2 defaults (all four signals denied in the consent regions,
 * waiting up to 500ms for an update, granted everywhere else), ads data
 * redaction and URL passthrough, then the stored choice as an update so a
 * returning visitor's tags fire with it. Plain ES5 so it runs inline anywhere;
 * `gtag` pushes the `arguments` object because GTM ignores arrays. The cookie
 * pattern is built from the same constants as parseConsent.
 */
export function consentDefaultsScript(): string {
  const denied = {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  };
  const granted = {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  };
  // The same decision as isConsentRegion, from the nt_geo cookie the Worker set on
  // this HTML response, so the default always matches whether the banner shows.
  // Google's own `region` list is not used: Google and Cloudflare can disagree on
  // a visitor's country, which left a visitor denied with no banner to grant from.
  const geoPattern = `(?:^|;\\s*)${GEO_COOKIE}=([^;]*)`;
  const cookiePattern = `(?:^|;\\s*)${CONSENT_COOKIE}=v${CONSENT_VERSION}\\.an([01])\\.ad([01])(?:;|$)`;
  return [
    "(function(){",
    "window.dataLayer=window.dataLayer||[];",
    "window.gtag=window.gtag||function(){window.dataLayer.push(arguments);};",
    "var g=window.gtag;",
    `var c=(new RegExp(${JSON.stringify(geoPattern)}).exec(document.cookie||'')||[])[1]||'';`,
    "c=String(c).toUpperCase();",
    `var inRegion=!/^[A-Z]{2}$/.test(c)||${JSON.stringify([...UNKNOWN_COUNTRIES])}.indexOf(c)>=0||${JSON.stringify([...CONSENT_REGIONS])}.indexOf(c)>=0;`,
    `g('consent','default',inRegion?${JSON.stringify(denied)}:${JSON.stringify(granted)});`,
    "g('set','ads_data_redaction',true);",
    "g('set','url_passthrough',true);",
    `var m=new RegExp(${JSON.stringify(cookiePattern)}).exec(document.cookie||'');`,
    "if(m){var a=m[1]==='1'?'granted':'denied',d=m[2]==='1'?'granted':'denied';",
    "g('consent','update',{analytics_storage:a,ad_storage:d,ad_user_data:d,ad_personalization:d});}",
    "})();",
  ].join("");
}

/**
 * Extra `mixpanel.init` options: opted out (and storing nothing) until
 * analytics is granted. `opt_out_persistence_by_default` matters only when
 * opting out by default, so it is always on.
 */
export function mixpanelConsentOptions(analyticsGranted: boolean): {
  opt_out_tracking_by_default: boolean;
  opt_out_persistence_by_default: boolean;
} {
  return {
    opt_out_tracking_by_default: !analyticsGranted,
    opt_out_persistence_by_default: true,
  };
}

/** The slice of mixpanel-browser the sync needs. */
export interface MixpanelOptInOut {
  has_opted_out_tracking(): boolean;
  opt_in_tracking(): void;
  opt_out_tracking(options: { delete_user: boolean }): void;
}

/**
 * Bring an initialised Mixpanel in line with the current choice. Opting out
 * never deletes the customer's Mixpanel profile (the SDK's default would).
 */
export function syncMixpanelConsent(
  mixpanel: MixpanelOptInOut,
  analyticsGranted: boolean,
): void {
  const optedOut = mixpanel.has_opted_out_tracking();
  if (analyticsGranted && optedOut) mixpanel.opt_in_tracking();
  if (!analyticsGranted && !optedOut)
    mixpanel.opt_out_tracking({ delete_user: false });
}

// ---- Browser helpers -------------------------------------------------------
/**
 * The browser half: read the current state from the page's cookies, save a
 * choice, and the two window events the banner and its "Cookie settings"
 * links use to talk across components (and across the Astro site's separate
 * script bundles). Every helper takes the window as a parameter so tests can
 * pass a fake one.
 */

/** Fired on window with the new ConsentChoice as `detail` after every save. */
export const CONSENT_CHANGE_EVENT = "nt:consent-change";
/** Fired on window by a "Cookie settings" link; the banner reopens on it. */
export const OPEN_COOKIE_SETTINGS_EVENT = "nt:cookie-settings";

/** The slice of `window` these helpers touch. */
export interface ConsentWindow extends EventTarget {
  document: { cookie: string };
  location: { hostname: string; protocol: string };
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
}

export interface ConsentState {
  /** The visitor's `nt_geo` country is one where we ask first (or unknown). */
  inRegion: boolean;
  /** The stored choice, if any. */
  choice: ConsentChoice | null;
  /** What applies now. */
  effective: ConsentChoice;
  showBanner: boolean;
}

export function readConsentState(cookies: string): ConsentState {
  const inRegion = isConsentRegion(readCookie(cookies, GEO_COOKIE));
  const choice = parseConsent(readCookie(cookies, CONSENT_COOKIE));
  return {
    inRegion,
    choice,
    effective: effectiveConsent(choice, inRegion),
    showBanner: shouldShowBanner(choice, inRegion),
  };
}

/**
 * Store the choice for 180 days, tell Google tags (`consent` `update`), push
 * `consent_update` for GTM triggers, and tell the page's own listeners (the
 * Mixpanel gate).
 */
export function saveConsent(choice: ConsentChoice, win: ConsentWindow): void {
  win.document.cookie = consentCookie(choice, {
    hostname: win.location.hostname,
    secure: win.location.protocol === "https:",
  });
  const layer = (win.dataLayer = win.dataLayer || []);
  const gtag =
    win.gtag ??
    function () {
      // GTM reads gtag() calls only as Arguments objects.
      layer.push(arguments);
    };
  const signals = gtagConsent(choice);
  gtag("consent", "update", signals);
  layer.push({
    event: "consent_update",
    consent_analytics: signals.analytics_storage,
    consent_advertising: signals.ad_storage,
  });
  win.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: choice }));
}

export function onConsentChange(
  listener: (choice: ConsentChoice) => void,
  win: ConsentWindow,
): () => void {
  const handler = (event: Event) =>
    listener((event as CustomEvent<ConsentChoice>).detail);
  win.addEventListener(CONSENT_CHANGE_EVENT, handler);
  return () => win.removeEventListener(CONSENT_CHANGE_EVENT, handler);
}

export function openCookieSettings(win: ConsentWindow): void {
  win.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}

export function onOpenCookieSettings(
  listener: () => void,
  win: ConsentWindow,
): () => void {
  win.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, listener);
  return () => win.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, listener);
}
